import React from 'react'
import { BooleanParam, useQueryParam, withDefault } from 'use-query-params'

import { Trans, t } from '@lingui/macro'
import dayjs from 'shared/dayjs'

import isMobile from 'ismobilejs'

import { Box, Flex, Heading, TagBeta, Spinner } from 'front-end/src/components'
import ActivityHistoryTable from './components/ActivityHistoryTable'
import ActivityHistoryStats from './components/ActivityHistoryStats'
import {
  DEFAULT_ACTIVITY_SEARCH_LIMIT,
  DEFAULT_TABLE_FILTERS
} from './activityHistoryConstants'
import ActivityHistoryFilter from './components/ActivityHistoryFilter'
import {
  ActivityHistoryOrderColumn,
  SqlOrderDirectionEnum
} from '../../graphql/private/PrivateSchemaTypes'
import ActivityHistoryMobileBlockerScreen from './components/ActivityHistoryMobileBlockerScreen'
import { useCPDFeatureFlag } from 'front-end/src/app/hooks/useFeatureFlags'
import {
  useActivityCpdGroupPermissionQuery,
  useCpdAggregatesQuery
} from 'front-end/src/pages/activity-history/ActivityHistory.codegen'
import { useActivityHistoryFiltersFromQuery } from './useActivityHistoryFiltersFromQuery'
import {
  useDateFilterContext,
  WithDateFilterContext
} from '../../components/common/dateFilter'
import { predefinedDateRanges } from 'front-end/src/components/utils/predefinedDateRanges'
import HeadTitle from 'front-end/src/components/common/head/HeadTitle'

const ActivityHistoryPageComponent = () => {
  const { phone } = isMobile()

  const { isCPDEnabledInAccountSettings } = useCPDFeatureFlag()
  const { data: groupPermissionData, loading: isGroupPermissionLoading } =
    useActivityCpdGroupPermissionQuery()
  const isMemberOfCpdGroup = !!groupPermissionData?.me?.isMemberOfCPDGroup
  const isCpdEnabled = isCPDEnabledInAccountSettings && isMemberOfCpdGroup

  const [queryFilters, setQueryFilters] = useActivityHistoryFiltersFromQuery()
  const [dateFilters, setDateFilters] = useDateFilterContext()
  const [isCpdOnlyFilter, setCpdOnlyFilter] = useQueryParam(
    'cpdOnly',
    withDefault(BooleanParam, false)
  )

  const tableFilters = {
    search: queryFilters.search?.substring(0, DEFAULT_ACTIVITY_SEARCH_LIMIT),
    orderColumn: ActivityHistoryOrderColumn[queryFilters.orderColumn]
      ? ActivityHistoryOrderColumn[queryFilters.orderColumn]
      : DEFAULT_TABLE_FILTERS.orderColumn,
    orderDirection: SqlOrderDirectionEnum[queryFilters.orderDirection]
      ? SqlOrderDirectionEnum[queryFilters.orderDirection]
      : DEFAULT_TABLE_FILTERS.orderDirection,
    page: queryFilters.page,
    limit: DEFAULT_TABLE_FILTERS.limit
  }

  // from and to filters parsed by useQueryParams as day with time at midnight
  // we should manually set them to start and end of the day.
  const dateFiltersAsIsoStr = React.useMemo(
    () => ({
      from: dayjs(dateFilters.from).startOf('day').toISOString(),
      to: dayjs(dateFilters.to).endOf('day').toISOString()
    }),
    [dateFilters.from, dateFilters.to]
  )

  const {
    data: aggregatesData,
    loading: isAggregatesLoading,
    refetch: refetchStats
  } = useCpdAggregatesQuery({
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
    variables: {
      from: dateFiltersAsIsoStr.from,
      to: dateFiltersAsIsoStr.to,
      fromDateTime: dateFiltersAsIsoStr.from,
      toDateTime: dateFiltersAsIsoStr.to
    }
  })

  const toggleCPDOnlyFilter = () => {
    setCpdOnlyFilter(!isCpdOnlyFilter)
    setQueryFilters((prevState) => ({ ...prevState, ...DEFAULT_TABLE_FILTERS }))
    refetchStats()
  }

  const updateTableFilters = (newTableFilters: typeof tableFilters) => {
    setQueryFilters((prevState) => ({ ...prevState, ...newTableFilters }))
  }

  if (isGroupPermissionLoading) return <Spinner />

  return (
    <>
      <HeadTitle title={t`Activity history`} />
      <Box className="panel">
        <Box
          className="panel-content"
          borderTop="none"
          paddingTop={['16px', '48px']}
          paddingBottom={['16px', '48px']}
        >
          <Box maxWidth="1200px" w="80%" m="0 auto">
            <Flex alignItems="center" mb="24px">
              <Heading
                fontSize="32px"
                data-test="Title"
                className="applyCustomFont"
              >
                <Trans>Activity history</Trans>
              </Heading>
              <TagBeta ml="13px" mr="27px" />
              {!phone && (
                <ActivityHistoryFilter
                  dateFilters={dateFilters}
                  setDateFilters={setDateFilters}
                  isCpdOnlyFilter={isCpdEnabled && isCpdOnlyFilter}
                  toggleCpdFilter={toggleCPDOnlyFilter}
                  isCpdEnabled={isCpdEnabled}
                />
              )}
            </Flex>
            {phone ? (
              <ActivityHistoryMobileBlockerScreen />
            ) : (
              <>
                <ActivityHistoryStats
                  statsData={aggregatesData?.me}
                  loading={isAggregatesLoading}
                  isCpdEnabled={isCpdEnabled}
                />
                <ActivityHistoryTable
                  dateFilters={dateFiltersAsIsoStr}
                  tableFilters={tableFilters}
                  refetchStats={refetchStats}
                  updateTableFilters={updateTableFilters}
                  isCpdOnlyFilter={isCpdEnabled && isCpdOnlyFilter}
                  isCpdEnabled={isCpdEnabled}
                />
              </>
            )}
          </Box>
        </Box>
      </Box>
    </>
  )
}

export const ActivityHistoryPage = () => {
  return (
    <WithDateFilterContext
      defaultValues={{
        period: predefinedDateRanges[1]
      }}
    >
      <ActivityHistoryPageComponent />
    </WithDateFilterContext>
  )
}
